<template>
	<div class="company" :key="id">
		<!-- Company Brand -->
		<section
			class="b360-grid-row preference-center-section preference-center-section--brand"
		>
			<TitleDescription
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 form-title-description"
				:title="companyBrandSection.title"
				:description="companyBrandSection.description"
			/>
			<CloudinaryUploader
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 form-title-description cloudinary-uploader"
				label="Company Logo"
				id="company-profile"
				:uri="currentState.companyLogo.imageUri"
				:disabled="roleIsNotActive('EditACompanyLogo')"
				@upload="updateLogo"
				@delete="deleteLogo"
			/>
			<div class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4">
				<ColorPicker
					class="colorpicker--company"
					title="Brand Colors"
					name="Color1"
					:color="currentState.companyColors.Color1"
					@input="updateColor"
					:disabled="roleIsNotActive('EditACompanyBrandColors')"
				/>
				<ColorPicker
					class="colorpicker--company"
					name="Color2"
					:color="currentState.companyColors.Color2"
					@input="updateColor"
					:disabled="roleIsNotActive('EditACompanyBrandColors')"
				/>
				<ColorPicker
					class="colorpicker--company"
					name="Color3"
					:color="currentState.companyColors.Color3"
					@input="updateColor"
					:disabled="roleIsNotActive('EditACompanyBrandColors')"
				/>
				<FontDropdown
					:name="companyFontFamily.name"
					label="Font Family"
					:options="fontOptionsArray"
					v-model="currentState.companyFontFamily.value"
					@input="updateFontFamily"
					:disabled="roleIsNotActive('EditACompanyFontFamily')"
				/>
			</div>
		</section>
		<!-- Company Details -->
		<section class="b360-grid-row preference-center-section">
			<TitleDescription
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 form-title-description"
				title="Company Details"
				description="Include your contact information."
			/>

			<FormInputs
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
				name="companyName"
				label="Company Name"
				type="text"
				placeholder="Your Company Name"
				:value="companyName"
				required
				disabled
			/>

			<FormInputs
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
				name="dba"
				label="Doing Business As"
				type="text"
				placeholder="Your DBA Name"
				:maxLength="100"
				:value="dba"
				disabled
			/>

			<div class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4">
				<FormInputs
					name="email"
					label="Email Address"
					type="text"
					placeholder="email@domain.com"
					:maxLength="100"
					v-model="$v.currentState.companyEmail.$model"
					required
					:disabled="roleIsNotActive('EditACompanyEmail')"
					:error="(isNoEmail || isInvalidEmail) && !roleIsNotActive('EditACompanyEmail')"
					data-test="email-input"
				/>
				<span data-test="no-email" class="error" v-if="isNoEmail && !roleIsNotActive('EditACompanyEmail')"
					>Email is required.
				</span>
				<span data-test="invalid-email" class="error" v-if="isInvalidEmail && !roleIsNotActive('EditACompanyEmail')"
					>Invalid email.</span
				>
			</div>

			<div class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4">
				<FormInputs
					class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
					name="websiteUrl"
					label="Website"
					data-test="website"
					type="text"
					placeholder="https://websitegoeshere.com"
					:maxLength="61"
					v-model="$v.currentState.websiteUrl.$model"
					required
					:disabled="roleIsNotActive('EditACompanyName')"
					:error="isNoWebsite && !roleIsNotActive('EditACompanyName')"
				/>
				<span data-test="no-website" class="error" v-if="isNoWebsite && !roleIsNotActive('EditACompanyName')"
					>Website is required.
				</span>
			</div>

			<FormInputs
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
				name="primaryPhoneNumber"
				label="Phone Number'"
				type="phone"
				placeholder="555-555-5555"
				:value="primaryPhoneNumber"
				:mask="[
					/[1-9]/,
					/\d/,
					/\d/,
					'-',
					/\d/,
					/\d/,
					/\d/,
					'-',
					/\d/,
					/\d/,
					/\d/,
					/\d/,
				]"
				required
				disabled
			/>

			<FormInputs
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
				name="nmlsNumber"
				label="NMLS"
				type="text"
				placeholder="1234567890"
				:maxLength="100"
				:value="nmlsNumber"
				required
				disabled
			/>
		</section>

		<!-- Mailing Address -->
		<section class="b360-grid-row preference-center-section">
			<TitleDescription
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 form-title-description"
				title="Mailing Address"
				description="Include your physical location."
			/>

			<FormInputs
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
				name="primaryAddress1"
				label="Address Line 1"
				type="text"
				placeholder="123 Main St"
				:maxLength="100"
				:value="primaryAddress1"
				required
				disabled
			/>

			<FormInputs
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
				name="primaryAddress2"
				label="Address Line 2"
				type="text"
				placeholder="Suite 313"
				:maxLength="100"
				:value="primaryAddress2"
				disabled
			/>

			<FormInputs
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
				name="primaryCity"
				label="City"
				type="text"
				placeholder="Anytown"
				:maxLength="50"
				:value="primaryCity"
				required
				disabled
			/>

			<StateDropdown
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
				name="primaryState"
				label="State"
				type="text"
				placeholder="MI"
				:value="primaryState"
				required
				disabled
			/>

			<FormInputs
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
				name="primaryZipCode"
				label="Zip Code"
				type="text"
				placeholder="12345"
				:maxLength="50"
				:value="primaryZipCode"
				required
				disabled
			/>
		</section>
		<!-- About (FAMB) Section -->
		<section
			class="b360-grid-row preference-center-section preference-center-section--about"
			v-if="hasFambFeature && hasAccessToFamb"
		>
			<TitleDescriptionLink
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 form-title-description"
				:title="aboutSection.title"
				:description="aboutSection.description"
				linkDescription="click here."
				@handle-click="aboutDescriptionClickHandler"
				:class="`title-description--span${aboutSection.gridSpan}`"
			/>
			<FormTextarea
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-8 b360-col-lg-8 about-input"
				:name="aboutSection.aboutInput.name"
				:label="aboutSection.aboutInput.label"
				:labelHidden="aboutSection.aboutInput.labelHidden"
				v-model="currentState.aboutCompany"
				:maxLength="aboutSection.aboutInput.maxLength"
				:showMaxLengthIndicator="true"
				:required="aboutSection.aboutInput.required"
				:disabled="mainProfileIsInternalUser || roleIsNotActive('EditACompanyAbout')"
			/>
		</section>
		<!-- Social Media Section -->
		<section
			class="b360-grid-row preference-center-section preference-center-section--social"
		>
			<TitleDescription
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 form-title-description"
				:title="socialMediaSection.title"
				:description="socialMediaSection.description"
			/>
			<div class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4">
				<FormInputs
					:id="socialMediaSection.facebook.name"
					:name="socialMediaSection.facebook.name"
					:label="socialMediaSection.facebook.label"
					:type="socialMediaSection.facebook.type"
					:placeholder="socialMediaSection.facebook.placeholder"
					:disabled="roleIsNotActive(socialMediaSection.facebook.permissionName)"
					:required="socialMediaSection.facebook.required"
					:maxLength="socialMediaSection.facebook.maxLength"
					:error="!isValidFacebook && !roleIsNotActive(socialMediaSection.facebook.permissionName)"
					v-model="currentState.socialMediaUrls.FaceBook"
					data-test="facebook-input"
				/>
				<span
					class="error"
					v-if="!isValidFacebook && !roleIsNotActive(socialMediaSection.facebook.permissionName)"
					data-test="invalid-facebook"
				>
					Please enter a valid Facebook account URL
				</span>
			</div>
			<div class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4">
				<FormInputs
					class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
					:id="socialMediaSection.linkedin.name"
					:name="socialMediaSection.linkedin.name"
					:label="socialMediaSection.linkedin.label"
					:type="socialMediaSection.linkedin.type"
					:placeholder="socialMediaSection.linkedin.placeholder"
					:disabled="roleIsNotActive(socialMediaSection.linkedin.permissionName)"
					:required="socialMediaSection.linkedin.required"
					:maxLength="socialMediaSection.linkedin.maxLength"
					:error="!isValidLinkedIn && !roleIsNotActive(socialMediaSection.linkedin.permissionName)"
					v-model="currentState.socialMediaUrls.LinkedIn"
					data-test="linkedin-input"
				/>
				<span
					class="error"
					v-if="!isValidLinkedIn && !roleIsNotActive(socialMediaSection.linkedin.permissionName)"
					data-test="invalid-linkedin"
				>
					Please enter a valid LinkedIn account URL
				</span>
			</div>
			<div class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4">
				<FormInputs
					:id="socialMediaSection.twitter.name"
					:name="socialMediaSection.twitter.name"
					:label="socialMediaSection.twitter.label"
					:type="socialMediaSection.twitter.type"
					:placeholder="socialMediaSection.twitter.placeholder"
					:disabled="roleIsNotActive(socialMediaSection.twitter.permissionName)"
					:required="socialMediaSection.twitter.required"
					:maxLength="socialMediaSection.twitter.maxLength"
					:error="!isValidTwitter && !roleIsNotActive(socialMediaSection.twitter.permissionName)"
					v-model="currentState.socialMediaUrls.Twitter"
					data-test="twitter-input"
				/>
				<span
					class="error"
					v-if="!isValidTwitter && !roleIsNotActive(socialMediaSection.twitter.permissionName)"
					data-test="invalid-twitter"
				>
					Please enter a valid Twitter account URL
				</span>
			</div>
			<div class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4">
				<FormInputs
					:id="socialMediaSection.instagram.name"
					:name="socialMediaSection.instagram.name"
					:label="socialMediaSection.instagram.label"
					:type="socialMediaSection.instagram.type"
					:placeholder="socialMediaSection.instagram.placeholder"
					:disabled="roleIsNotActive(socialMediaSection.instagram.permissionName)"
					:required="socialMediaSection.instagram.required"
					:maxLength="socialMediaSection.instagram.maxLength"
					:error="!isValidInstagram && !roleIsNotActive(socialMediaSection.instagram.permissionName)"
					v-model="currentState.socialMediaUrls.Instagram"
					data-test="instagram-input"
				/>
				<span
					class="error"
					v-if="!isValidInstagram && !roleIsNotActive(socialMediaSection.instagram.permissionName)"
					data-test="invalid-instagram"
				>
					Please enter a valid Instagram account URL
				</span>
			</div>
			<div class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4">
				<FormInputs
					:id="socialMediaSection.tikTok.name"
					:name="socialMediaSection.tikTok.name"
					:label="socialMediaSection.tikTok.label"
					:type="socialMediaSection.tikTok.type"
					:placeholder="socialMediaSection.tikTok.placeholder"
					:disabled="roleIsNotActive(socialMediaSection.tikTok.permissionName)"
					:required="socialMediaSection.tikTok.required"
					:maxLength="socialMediaSection.tikTok.maxLength"
					v-model="currentState.socialMediaUrls.TikTok"
					:error="!isValidTikTok && !roleIsNotActive(socialMediaSection.tikTok.permissionName)"
					data-test="tikTok-input"
				/>
				<span
					class="error"
					v-if="!isValidTikTok && !roleIsNotActive(socialMediaSection.tikTok.permissionName)"
					data-test="invalid-tik-tok"
				>
					Please enter a valid TikTok account URL
				</span>
			</div>
			<div class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4">
				<FormInputs
					:id="socialMediaSection.googleMyBusiness.name"
					:name="socialMediaSection.googleMyBusiness.name"
					:label="socialMediaSection.googleMyBusiness.label"
					:type="socialMediaSection.googleMyBusiness.type"
					tooltip="Select 'Ask for Reviews' in your Google Business Profile to copy your review link."
					:placeholder="socialMediaSection.googleMyBusiness.placeholder"
					:disabled="roleIsNotActive(socialMediaSection.googleMyBusiness.permissionName)"
					v-model="currentState.socialMediaUrls.GoogleMyBusiness"
					:error="!isValidGoogleMyBusiness && !roleIsNotActive(socialMediaSection.googleMyBusiness.permissionName)"
					:required="socialMediaSection.googleMyBusiness.required"
					:maxLength="socialMediaSection.googleMyBusiness.maxLength"
					data-test="googleMyBusiness-input"
				/>
				<span
					class="error"
					v-if="!isValidGoogleMyBusiness && !roleIsNotActive(socialMediaSection.googleMyBusiness.permissionName)"
					data-test="invalid-googleMyBusiness"
				>
					Invalid. Google My Business links must follow this format:
					'g.page/r/ABcDEF2LMg/review'
				</span>
			</div>
		</section>
		<SaveButton
			:waiting="savingState"
			:saved="stateSaved"
			@handle-click="saveCompanyProfile()"
			:disabled="conditionsForSavingMet"
		/>
	</div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';

import CloudinaryUploader from '@/components/preference-center/CloudinaryUploader.vue';
import TitleDescription from '@/components/global/TitleDescription.vue';
import TitleDescriptionLink from '@/components/global/TitleDescriptionLink.vue';
import FormInputs from '@/components/global/FormInputs.vue';
import FormTextarea from '@/components/global/FormTextarea.vue';
import ColorPicker from '@/components/brand-builder/ColorPicker.vue';
import FontDropdown from '@/components/preference-center/FontDropdowns.vue';
import StateDropdown from '@/components/preference-center/StateDropdowns.vue';
import SaveButton from '@/components/preference-center/SaveButton.vue';

import {
	required,
	email,
	helpers,
	maxLength,
} from 'vuelidate/lib/validators';
import { cloneDeep, isEqual } from 'lodash';
import colorExtractor from '@/util/color-extractor';

import {
	SOCIAL_MEDIA_DETAILS_COMPANY,
	FONT_OPTIONS_ARRAY,
	SOCIAL_MEDIA_URL,
	REVIEW_ME,
} from './constants';
import { ERROR_DETAILS } from '@/constants/error-details';

// This function is used to validate the social media links with vuelidate
const isValidSocial =
	(start = '', end = '') =>
	(link) => {
		link = removeHttps(link);
		return !helpers.req(link) || (link.startsWith(start) && link.endsWith(end));
	}

	const removeHttps = url => url.startsWith('https://') ? url.substring(8) : url;

export default {
	name: 'PreferenceCenterCompany',
	components: {
		CloudinaryUploader,
		TitleDescription,
		TitleDescriptionLink,
		FormInputs,
		FormTextarea,
		ColorPicker,
		FontDropdown,
		StateDropdown,
		SaveButton,
	},
	data() {
		return {
			originalState: {},
			currentState: {},
			fontOptionsArray: FONT_OPTIONS_ARRAY.toSorted((a, b) =>
				a.localeCompare(b),
			),
			companyBrandSection: {
				id: 1,
				title: 'Company Brand',
				description:
					'Include your logo in all your marketing materials to build brand awareness and recognition.',
			},
			aboutSection: {
				id: 2,
				title: 'About',
				description:
					'Include an ‘about’ description that will appear in your MortgageMatchup.com company profile listing. To view your profile settings,',
				gridSpan: 2,
				aboutInput: {
					name: 'aboutCompany',
					label: 'About',
					labelHidden: true,
					type: 'textarea',
					maxLength: 1024,
					required: false,
					disabled: false,
				},
			},
			socialMediaSection: {
				id: 3,
				title: 'Social Media',
				description:
					'Add your social media accounts to include them on your MortgageMatchup.com profile.',
				gridSpan: 2,
				...SOCIAL_MEDIA_DETAILS_COMPANY,
			},
			socialMediaDomains: SOCIAL_MEDIA_URL,
			originalGMB: '',
			turnOnReviewMe: false,
			doNotSave: false,
			savingState: false,
			stateSaved: false,
		};
	},
	validations() {
		return {
			currentState: {
				companyEmail: {
					required,
					email,
				},
				websiteUrl: {
					required,
				},
				aboutCompany: {
					maxLengthValue: maxLength(1024),
				},
				socialMediaUrls: {
					FaceBook: {
						isValid: isValidSocial('www.facebook.com/'),
					},
					LinkedIn: {
						isValid: isValidSocial('www.linkedin.com/'),
					},
					Twitter: {
						isValid: isValidSocial('www.twitter.com/'),
					},
					Instagram: {
						isValid: isValidSocial('www.instagram.com/'),
					},
					TikTok: {
						isValid: isValidSocial('www.tiktok.com/'),
					},
					GoogleMyBusiness: {
						isValid: isValidSocial('g.page/', '/review'),
					},
				},
			},
		};
	},
	computed: {
		...mapGetters(['hasAccessToFamb', 'roleIsNotActive']),
		...mapState({
			id: (state) => state.companyProfile.id,
			// Company Brand
			companyLogo: (state) =>
				state.companyProfile.companyLogo !== null
					? state.companyProfile.companyLogo
					: {},
			brandItems: (state) => state.companyProfile.brandItems,
			// Company Details
			companyName: (state) => state.companyProfile.companyName,
			dba: (state) => state.companyProfile.dba,
			companyEmail: (state) => state.companyProfile.email,
			websiteUrl: (state) => state.companyProfile.websiteUrl,
			primaryPhoneNumber: (state) => state.companyProfile.primaryPhoneNumber,
			nmlsNumber: (state) => state.companyProfile.nmlsNumber,
			// Mailing Address
			primaryAddress1: (state) => state.companyProfile.primaryAddress1,
			primaryAddress2: (state) => state.companyProfile.primaryAddress2,
			primaryCity: (state) => state.companyProfile.primaryCity,
			primaryState: (state) => state.companyProfile.primaryState,
			primaryZipCode: (state) => state.companyProfile.primaryZipCode,
			// About (FAMB) Section
			aboutCompany: (state) => state.companyProfile.aboutCompany,
			hasFambFeature: (state) => state.companyProfile.fambFeatureFlagIndicator,
			// Social Media Section
			socialMediaDetails: (state) => state.companyProfile.socialMediaDetails,
			// profile
			contactId: (state) => state.profile.contactId,
			// profileSwitcher
			disableSave: (state) =>
				state.profileSwitcher.profileSelector.isInternalUser,
			mainProfileIsInternalUser: (state) =>
				state.profileSwitcher.mainProfileSelector.isInternalUser,
			// userAlerts
			preferences: (state) => state.userAlerts.preferences,
		}),
		companyColors() {
			if (!this.brandItems || !this.brandItems.length) {
				return {
					Color1: '',
					Color2: '',
					Color3: '',
				};
			}
			return this.brandItems.reduce((acc, cur) => {
				if (cur.name.includes('Color')) {
					return { ...acc, [cur.name]: cur.value };
				} else {
					return acc;
				}
			}, {});
		},
		companyFontFamily() {
				const name = 'HeaderFontFamily';
				if (!this.brandItems || !this.brandItems.length) {
						return { name, value: '' };
				}
				const headerFont = this.brandItems.find((el) => el.name === name);
				return headerFont || { name, value: '' };
		},
		socialMediaUrls() {
			let urls = {
				FaceBook: '',
				LinkedIn: '',
				Twitter: '',
				Instagram: '',
				GoogleMyBusiness: '',
				TikTok: '',
			};
			if (this.socialMediaDetails && this.socialMediaDetails.length) {
				this.socialMediaDetails.forEach((el) => {
					urls[el.type] = el.value;
				});
			}
			return urls;
		},
		isInvalidEmail() {
			return !this.$v.currentState.companyEmail.email;
		},
		isNoEmail() {
			return !this.$v.currentState.companyEmail.required;
		},
		isNoWebsite() {
			return !this.$v.currentState.websiteUrl.required;
		},
		isValidInstagram() {
			return !this.$v.currentState.socialMediaUrls.Instagram.$invalid;
		},
		isValidTwitter() {
			return !this.$v.currentState.socialMediaUrls.Twitter.$invalid;
		},
		isValidFacebook() {
			return !this.$v.currentState.socialMediaUrls.FaceBook.$invalid;
		},
		isValidLinkedIn() {
			return !this.$v.currentState.socialMediaUrls.LinkedIn.$invalid;
		},
		isValidTikTok() {
			return !this.$v.currentState.socialMediaUrls.TikTok.$invalid;
		},
		isValidGoogleMyBusiness() {
			return !this.$v.currentState.socialMediaUrls.GoogleMyBusiness.$invalid;
		},
		isGoogleMyBusinessAddDelete() {
			const originalState = this.originalState.socialMediaUrls.GoogleMyBusiness;
			const currentState = this.currentState.socialMediaUrls.GoogleMyBusiness;

			if (originalState === '' && currentState !== '') {
				return 'add';
			} else if (originalState !== '' && currentState === '') {
				return 'delete';
			} else {
				return '';
			}
		},
		socialMediasAreValid() {
			return !this.$v.currentState.socialMediaUrls.$invalid;
		},
		conditionsForSavingMet() {
			return this.disableSave || this.$v.$invalid || !this.isUpdatedState;
		},
		isUpdatedState() {
			return !isEqual(this.originalState, this.currentState);
		},
	},
	methods: {
		...mapActions([
			'saveAlerts',
			'setAllAlertPreferences',
			'showApiErrorModal',
			'updateCompanyProfile',
		]),
		copyUpdatableState() {
			return cloneDeep({
				companyLogo: this.companyLogo,
				companyColors: this.companyColors,
				companyFontFamily: this.companyFontFamily,
				companyEmail: this.companyEmail,
				websiteUrl: this.websiteUrl,
				aboutCompany: this.aboutCompany,
				socialMediaUrls: this.socialMediaUrls,
			});
		},
		async updateLogo(logo) {
			this.currentState.companyLogo = {
				imageFormat: logo.format,
				referenceId: logo.publicId,
				imageUri: logo.secureUrl,
			};
			this.currentState.companyColors =
				await colorExtractor.updateColorsCompanyProfile(logo.secureUrl);
		},
		deleteLogo() {
			this.currentState.companyLogo = {};
		},
		updateColor({ fieldName, value }) {
			this.currentState.companyColors[fieldName] = value;
		},
		updateFontFamily({ value }) {
			this.currentState.companyFontFamily.value = value;
		},
		aboutDescriptionClickHandler() {
			this.$router.push({
				path: '/preference-center/mortgagematchup',
			});
		},
		async updateReviewMeAlert(turnOnReviewMe) {
			const filteredAlertPreferences = cloneDeep(
				this.preferences.filter((alert) => alert.name !== 'ReviewMe'),
			);

			const newReviewMeAlert = turnOnReviewMe
				? REVIEW_ME.ENABLED
				: REVIEW_ME.DISABLED;

			const newAlertPreferences = [
				...filteredAlertPreferences,
				newReviewMeAlert,
			];

			try {
				await this.saveAlerts({
					contactId: this.contactId,
					preferences: newAlertPreferences,
				});
				this.setAllAlertPreferences(newAlertPreferences);
			} catch (e) {
				const error = new Error('googleMyBusinessAlert');
				return Promise.reject(error);
			}
		},
		async GoogleMyBusinessAddDelete() {
			if (this.isGoogleMyBusinessAddDelete === 'add') {
				const answer = window.confirm(
					'You are adding a Google My Business link. Doing so will turn on and enable the Review Me alert. To turn the alert off, please navigate to the Alerts tab to disable the Review Me alert.',
				);
				if (answer) {
					await this.updateReviewMeAlert(true);
				} else {
					this.doNotSave = true;
				}
			} else if (this.isGoogleMyBusinessAddDelete === 'delete') {
				const answer = window.confirm(
					'You are removing your Google My Business link. Doing so will turn off and disable the Review Me alert. Are you sure you want to continue?',
				);
				if (answer) {
					await this.updateReviewMeAlert(false);
				} else {
					this.doNotSave = true;
				}
			}
		},
		getNewBrandItems(font, colors) {
			return [
				font,
				...Object.entries(colors).reduce(
					(acc, [name, value]) => [...acc, { name, value }],
					[],
				),
			];
		},
		getNewSocialMediaDetails(urls) {
			return Object.entries(urls).reduce(
				(acc, [type, value]) => [...acc, { type, value }],
				[],
			);
		},
		async saveCompanyProfile() {
			try {
				this.savingState = true;

				if (this.isGoogleMyBusinessAddDelete) {
					await this.GoogleMyBusinessAddDelete();
				}

				if (this.doNotSave) {
					this.currentState.socialMediaUrls.GoogleMyBusiness =
						this.originalState.socialMediaUrls.GoogleMyBusiness;
					this.doNotSave = false;
					this.savingState = false;
					return;
				}

				const updatedCompanyProfile = {
					companyLogo: this.currentState.companyLogo,
					brandItems: this.getNewBrandItems(
						this.currentState.companyFontFamily,
						this.currentState.companyColors,
					),
					email: this.currentState.companyEmail,
					websiteUrl: this.currentState.websiteUrl,
					aboutCompany: this.currentState.aboutCompany,
					socialMediaDetails: this.getNewSocialMediaDetails(
						this.currentState.socialMediaUrls,
					),
				};

				await this.updateCompanyProfile(updatedCompanyProfile);

				this.savingState = false;
				this.stateSaved = true;
				this.originalState = cloneDeep(this.currentState);
				setTimeout(() => {
					this.stateSaved = false;
				}, 1000);
			} catch (error) {
				this.showApiErrorModal(ERROR_DETAILS.PREFERENCE_CENTER_COMPANY_ERROR);
				this.savingState = false;
			}
		},
	},
	created() {
		this.originalState = this.copyUpdatableState();
		this.currentState = this.copyUpdatableState();
	},
	beforeRouteLeave(to, from, next) {
		next(
			this.isUpdatedState
				? window.confirm(
						'There are unsaved changes in the form.  Are you sure you want to continue?',
				  )
				: true,
		);
	},
};
</script>

<style scoped lang="scss">
.preference-center-section--brand,
.preference-center-section--about {
	.title-description {
		grid-row-end: span 1;
	}
}

.preference-center-section--social .title-description {
	grid-row-end: span 3;
}

.cloudinary-uploader {
	@include rem('margin-bottom', 2px);
}
.color-picker-wrapper.colorpicker--company:nth-of-type(3) {
	@include rem('margin-bottom', 20px);
}

/deep/ .about-input textarea {
	min-height: 120px;
}
.error {
	color: $b360-color--red__error;
}
</style>
